<template>
  <div class="app-container">
    <Header />

    <div class="blog-content-container">
      <div :class="mainContentClass">
        <div class="blog-header">
          <span class="blog-date">{{ date }}</span>
          <h1 class="blog-title">{{ title }}</h1>
          <div class="blog-meta">
            <div class="author-info">
              <img
                :src="authorAvatar || '/blog_assets/logo.png'"
                class="author-avatar"
              />
              <span class="author-name">{{ author || "Skyhost Team" }}</span>
            </div>
          </div>
        </div>

        <div class="curriculum-blog">
          <div class="blog-hero-image">
            <img :src="heroImage" alt="Blog hero image" class="hero-image" />
          </div>
          <section class="section-content">
            <p>
              When you bring a live drone tour into your classroom, you're not
              just showing pretty pictures—you're weaving real‑world exploration
              into your learning goals. Aligning each flight with recognized
              standards helps you justify the experience, reinforce key
              concepts, and measure student growth. Below, you'll find how
              Skyhost's destinations map directly to subjects and topics across
              all grade bands, so you can see exactly where each tour fits into
              your curriculum.
            </p>
          </section>

          <section class="blog-section importance-section">
            <div class="section-content">
              <h2>Why Standards Alignment Matters</h2>
              <p>
                Teachers juggle tight schedules, district requirements, and the
                need to engage every learner. When a drone tour is explicitly
                tied to a learning objective—say, demonstrating erosion
                processes or examining historical landmarks—it becomes more than
                "just a field trip." It's a standards‑based instructional tool
                that:
              </p>
              <ul class="benefits-list">
                <li>
                  <strong>Supports your lesson plans</strong> by reinforcing
                  specific terminology and concepts
                </li>
                <li>
                  <strong>Simplifies assessment</strong>, since you can craft
                  exit tickets around the same objectives
                </li>
                <li>
                  <strong>Builds administrator confidence</strong> that the
                  experience is academically rigorous
                </li>
                <li>
                  <strong>Boosts student motivation</strong>, knowing that
                  "seeing it live" is directly tied to what they need to learn
                </li>
              </ul>
            </div>
          </section>

          <section class="blog-section curriculum-map-section">
            <div class="section-content">
              <h2>From First Grade to Senior Year: A Full Curriculum Map</h2>
              <p>
                Below is a comprehensive table of grade levels, subjects, and
                topics that pair perfectly with Skyhost's live drone tours. Feel
                free to add your own local units or tailor the themes to fit
                your state standards.
              </p>

              <div class="grade-section">
                <h3>Lower School (Grades 1–4)</h3>
                <div class="table-container">
                  <table class="curriculum-table">
                    <thead>
                      <tr>
                        <th>Subject</th>
                        <th>Topic</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Social Studies</td>
                        <td>Basic Landforms</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Water Bodies (Lakes, Ponds)</td>
                      </tr>
                      <tr>
                        <td>History</td>
                        <td>Local Historical Landmarks</td>
                      </tr>
                      <tr>
                        <td>History</td>
                        <td>Historic Buildings and Sites</td>
                      </tr>
                      <tr>
                        <td>History</td>
                        <td>Indigenous Heritage Sites</td>
                      </tr>
                      <tr>
                        <td>History</td>
                        <td>Historical Parks and Living History Museums</td>
                      </tr>
                      <tr>
                        <td>Social Studies</td>
                        <td>Weather and Seasons (Intro)</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Erosion by Wind</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Erosion by Water</td>
                      </tr>
                      <tr>
                        <td>Social Studies</td>
                        <td>Major Landforms</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Natural Hazards (Introductory)</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Weathering Processes</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Deposition (Sedimentation)</td>
                      </tr>
                      <tr>
                        <td>Social Studies</td>
                        <td>Human Adaptation to Environment (Local Impact)</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Soil Formation and Weathering Changes</td>
                      </tr>
                      <tr>
                        <td>Social Studies</td>
                        <td>Regions and Landforms of the U.S.</td>
                      </tr>
                      <tr>
                        <td><em>Your Topics</em></td>
                        <td><em>Add your own subjects / topics</em></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="grade-section">
                <h3>Middle School (Grades 5–8)</h3>
                <div class="table-container">
                  <table class="curriculum-table">
                    <thead>
                      <tr>
                        <th>Subject</th>
                        <th>Topic</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Science</td>
                        <td>Weathering, Erosion, and Deposition</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Plate Tectonics and Landform Creation</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Ecosystems and Biodiversity</td>
                      </tr>
                      <tr>
                        <td>Social Studies</td>
                        <td>Human Adaptation to Environment</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Waterfalls</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Natural Hazards and Disaster Impacts</td>
                      </tr>
                      <tr>
                        <td>Social Studies</td>
                        <td>Dams and Reservoirs</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Volcanoes (Introductory and Remnants)</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Sand Dunes</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Wind Farms (Renewable Energy)</td>
                      </tr>
                      <tr>
                        <td>Science</td>
                        <td>Eolian Features</td>
                      </tr>
                      <tr>
                        <td>History</td>
                        <td>Colonial and Revolutionary War Sites</td>
                      </tr>
                      <tr>
                        <td>History</td>
                        <td>Historic Urban Areas and Districts</td>
                      </tr>
                      <tr>
                        <td>History</td>
                        <td>Transportation and Industrial Heritage</td>
                      </tr>
                      <tr>
                        <td>History</td>
                        <td>Military Forts and Castles</td>
                      </tr>
                      <tr>
                        <td><em>Your Topics</em></td>
                        <td><em>Add your own subjects / topics</em></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="grade-section">
                <h3>High School (Grades 9–12)</h3>
                <div class="table-container">
                  <table class="curriculum-table">
                    <thead>
                      <tr>
                        <th>Subject / Discipline</th>
                        <th>Topic</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Earth & Space Science</td>
                        <td>Advanced Landform Formation</td>
                      </tr>
                      <tr>
                        <td>Environmental Science</td>
                        <td>Watersheds and Wetlands</td>
                      </tr>
                      <tr>
                        <td>Environmental Science</td>
                        <td>Agricultural Patterns and Land Use</td>
                      </tr>
                      <tr>
                        <td>Environmental Science / Ecology</td>
                        <td>Forest Canopy and Biodiversity Surveys</td>
                      </tr>
                      <tr>
                        <td>Geography / Social Studies</td>
                        <td>Coastal Systems and Climate Change</td>
                      </tr>
                      <tr>
                        <td>U.S. History / Social Studies</td>
                        <td>Engineering Feats: Dams and Infrastructure</td>
                      </tr>
                      <tr>
                        <td>Science / Geography</td>
                        <td>Volcanoes (Active, Dormant, Extinct)</td>
                      </tr>
                      <tr>
                        <td>Science / Renewable Energy</td>
                        <td>Wind Farms and Renewable Energy Landscapes</td>
                      </tr>
                      <tr>
                        <td>Environmental Science / Geomorphology</td>
                        <td>Sand Dunes and Desertification</td>
                      </tr>
                      <tr>
                        <td>Earth & Space Science</td>
                        <td>Meteor / Impact Craters</td>
                      </tr>
                      <tr>
                        <td>Earth & Space Science</td>
                        <td>Karst Topography</td>
                      </tr>
                      <tr>
                        <td>Earth & Space Science</td>
                        <td>Glacial Landforms</td>
                      </tr>
                      <tr>
                        <td>Science / Geography</td>
                        <td>
                          Fluvial Features (Meanders, Oxbow Lakes, River
                          Terraces)
                        </td>
                      </tr>
                      <tr>
                        <td>Geography / Environmental Science</td>
                        <td>Coastal Reef Systems and Atolls</td>
                      </tr>
                      <tr>
                        <td>Science / Geography</td>
                        <td>Lava Flows</td>
                      </tr>
                      <tr>
                        <td>Earth & Space Science</td>
                        <td>Avalanche Zones</td>
                      </tr>
                      <tr>
                        <td>Earth & Space Science</td>
                        <td>Uplifted Marine Terraces</td>
                      </tr>
                      <tr>
                        <td>Earth & Space Science</td>
                        <td>Salt Flats</td>
                      </tr>
                      <tr>
                        <td>Science / Geography</td>
                        <td>Natural Arches and Bridges</td>
                      </tr>
                      <tr>
                        <td>Environmental Science / Ecology</td>
                        <td>Fossil Sites / Paleontological Landscapes</td>
                      </tr>
                      <tr>
                        <td>Earth & Space Science</td>
                        <td>Rift Valleys</td>
                      </tr>
                      <tr>
                        <td>Geography / Environmental Science</td>
                        <td>Tidal Flats and Mudflats</td>
                      </tr>
                      <tr>
                        <td>History</td>
                        <td>National Historic Landmarks and Parks</td>
                      </tr>
                      <tr>
                        <td>History</td>
                        <td>Historic Urban Development and Cityscapes</td>
                      </tr>
                      <tr>
                        <td>History</td>
                        <td>Archaeological Sites</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          <section class="blog-section cta-section" style="border-bottom: none">
            <div class="section-content">
              <h2>Bring Standards-Aligned Tours to Your Classroom</h2>
              <p>
                Ready to integrate these standards-aligned drone experiences
                into your curriculum? Contact our education specialists today to
                discuss your grade level's specific objectives and discover how
                we can create a tailor-made exploration for your students.
              </p>
              <p>
                Remember: Every Skyhost educational tour comes with pre- and
                post-flight activities, assessment suggestions, and a
                certificate of participation for each student. Make learning
                unforgettable with a virtual journey that brings textbook
                concepts to vivid life.
              </p>
            </div>
          </section>
        </div>

        <div class="blog-footer">
          <div class="blog-tags" v-if="tags && tags.length">
            <span class="tag-label">Tags:</span>
            <span v-for="(tag, index) in tags" :key="index" class="blog-tag">
              {{ tag }}
            </span>
          </div>

          <div class="share-section">
            <span class="share-label">Share this article:</span>
            <div class="share-icons">
              <vue-feather
                type="twitter"
                size="18"
                class="share-icon"
                @click="shareOnTwitter"
              ></vue-feather>
              <vue-feather
                type="facebook"
                size="18"
                class="share-icon"
                @click="shareOnFacebook"
              ></vue-feather>
              <vue-feather
                type="linkedin"
                size="18"
                class="share-icon"
                @click="shareOnLinkedIn"
              ></vue-feather>
              <vue-feather
                type="mail"
                size="18"
                class="share-icon"
                @click="shareByEmail"
              ></vue-feather>
            </div>
          </div>

          <div class="related-posts" v-if="relatedBlogs.length">
            <h3 class="related-title">Related articles</h3>
            <div class="related-grid">
              <div
                v-for="relatedBlog in relatedBlogs"
                :key="relatedBlog.id"
                class="related-card"
                @click="navigateToBlog(relatedBlog.id)"
              >
                <img
                  :src="relatedBlog.image"
                  alt="Related blog image"
                  class="related-image"
                />
                <div class="related-content">
                  <h4 class="related-blog-title">{{ relatedBlog.title }}</h4>
                  <span class="related-date">{{ relatedBlog.date }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="back-to-blogs">
            <button class="blog-btn" @click="navigateToBlogs">
              <vue-feather
                type="arrow-left"
                size="16"
                class="back-icon"
              ></vue-feather>
              Back to all blogs
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "EducationBlogCurriculum",
  components: {
    Header,
    Footer,
  },
  data() {
    const blogId = "education-curriculum";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar,
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
    shareOnTwitter() {
      const text = `${this.title} - Check out this article by Skyhost`;
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(this.currentPageUrl)}`;
      window.open(url, "_blank");
    },
    shareOnFacebook() {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareByEmail() {
      const subject = encodeURIComponent(this.title);
      const body = encodeURIComponent(
        `I thought you might find this article interesting: ${this.title}\n\n${this.currentPageUrl}`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    this.relatedBlogs = Object.values(blogsRegistry)
      .filter((blog) => blog.id !== this.id)
      .slice(0, 2); // Limit to 2 related blogs
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });

    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}

.blog-header {
  margin-bottom: 2rem;
}

.blog-date {
  color: #666;
  font-size: 1rem;
}

.blog-title {
  font-size: 2.5rem;
  margin: 1rem 0;
  color: #000;
  line-height: 1.3;
}

.blog-meta {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 20px;
  height: 20px;
  /* border-radius: 50%; */
  margin-right: 0.8rem;
  object-fit: contain;
}

.author-name {
  font-weight: 500;
  color: #333;
}

.blog-hero-image {
  margin-bottom: 2rem;
  border-radius: 8px;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  max-height: 500px;
  border-radius: 8px;
}

.blog-image-container {
  height: 200px;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.curriculum-blog {
  font-family: "Inter", "Helvetica", sans-serif;
  line-height: 1.8;
  color: #333;
}

.blog-section {
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #f0f0f0;
}

.section-content {
  margin-bottom: 2rem;
}

h2 {
  font-size: 2rem;
  margin: 1.5rem 0;
  color: #000;
  font-weight: 600;
}

h3 {
  font-size: 1.5rem;
  margin: 1.5rem 0 1rem;
  color: #000;
  font-weight: 500;
}

p {
  margin-bottom: 1.5rem;
}

.benefits-list {
  margin: 1.5rem 0 1.5rem 1.5rem;
}

.benefits-list li {
  margin-bottom: 1rem;
}

.grade-section {
  margin-bottom: 3rem;
}

.table-container {
  overflow-x: auto;
  margin-bottom: 2rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.curriculum-table {
  width: 100%;
  border-collapse: collapse;
}

.curriculum-table th {
  background-color: #f8f9fa;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid #eee;
}

.curriculum-table td {
  padding: 0.8rem 1rem;
  border-bottom: 1px solid #f0f0f0;
}

.curriculum-table tr:hover {
  background-color: #f8f9fa;
}

.blog-footer {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
}

.blog-tags {
  margin-bottom: 2rem;
}

.tag-label {
  font-weight: 500;
  margin-right: 0.5rem;
}

.blog-tag {
  display: inline-block;
  padding: 0.3rem 0.8rem;
  margin: 0.3rem;
  background-color: #f0f0f0;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #666;
}

.share-section {
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
}

.share-label {
  font-weight: 500;
  margin-right: 1rem;
}

.share-icons {
  display: flex;
  gap: 1rem;
}

.share-icon {
  cursor: pointer;
  color: #555;
}

.share-icon:hover {
  color: #1565c0;
}

.related-posts {
  margin-bottom: 3rem;
}

.related-title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #000;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.related-card {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: transform 0.3s ease;
}

.related-card:hover {
  transform: translateY(-3px);
}

.related-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.related-content {
  padding: 1rem;
}

.related-blog-title {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #000;
}

.related-date {
  font-size: 0.8rem;
  color: #666;
}

.back-to-blogs {
  margin-top: 3rem;
  text-align: center;
}

.blog-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  background-color: #f0f0f0;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.blog-btn:hover {
  background-color: #e0e0e0;
}

.back-icon {
  margin-right: 0.5rem;
}

@media (max-width: 768px) {
  .curriculum-table {
    font-size: 0.9rem;
  }

  .blog-title {
    font-size: 2rem;
  }

  .related-grid {
    grid-template-columns: 1fr;
  }
}
</style>
